import { z } from 'zod';

import { AntiDilutionBase } from '../../../types/share-classes.types';
import { getTranslation } from '../../../utils/get-translation.util';

export const MAX_FILE_SIZE = 1024 * 1024 * 10;

export const ACCEPTED_FILE_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'image/png',
  'image/jpeg',
];

export const fileSchemaMessage = getTranslation('FILE_SIZE_TOO_LARGE');
export const formSchemaStepOne = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: getTranslation('REQUIRED') })
    .max(40, 'Share class name must be up to 40 characters'),
  creationDate: z.coerce.date(),
  votingRight: z
    .object({
      enabled: z.boolean(),
      value: z.coerce
        .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
        .min(1, { message: ' ' })
        .max(100000000, { message: ' ' }),
    })
    .optional(),
  conversionRatio: z
    .object({
      enabled: z.boolean(),
      value: z.coerce
        .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
        .min(1, { message: ' ' })
        .max(100000000, { message: ' ' }),
    })
    .optional(),
  dividendRightEnabled: z.boolean(),
});

const participating = z.union([
  z
    .object({
      enabled: z.boolean(),
      capValue: z.coerce
        .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
        .min(0.001, { message: getTranslation('REQUIRED') })
        .max(999999999999999, { message: 'Please enter a valid cap value' }),
      interest: z.coerce
        .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
        .max(100, { message: 'Please enter a valid interest rate' })
        .optional(),
      daysPersYear: z.undefined().optional(),
    })
    .refine(
      ({ interest, daysPersYear }) => (interest && daysPersYear) || (!interest && !daysPersYear),
    ),
  z.object({
    enabled: z.boolean(),
    capValue: z.coerce
      .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
      .min(0.001, { message: getTranslation('REQUIRED') })
      .max(999999999999999, { message: 'Please enter a valid cap value' }),
    interest: z.coerce
      .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
      .min(1, { message: getTranslation('REQUIRED') })
      .max(100, { message: 'Please enter a valid interest rate' }),
    daysPersYear: z.string(),
  }),
]);
export const fromSchemaStepTwo = z
  .object({
    liquidityPreferences: z
      .object({
        enabled: z.boolean(),
        seniority: z.coerce
          .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
          .min(1, { message: getTranslation('REQUIRED') })
          .max(99, { message: 'Please enter a valid seniority value' }),
        multiple: z.coerce
          .number()
          .min(0.001, { message: getTranslation('REQUIRED') })
          .max(999, { message: 'Please enter a valid multiple value' }),
        participating: participating.optional(),
      })
      .optional(),
  })
  .optional();

export const formSchemaStepThree = z
  .object({
    antiDilutionRights: z
      .object({
        enabled: z.boolean(),
        base: z.enum(AntiDilutionBase),
      })
      .optional(),
  })
  .optional();

export const fromSchemaStepFour = z
  .object({
    additionalNotes: z
      .string()
      .max(3000, getTranslation('MAXIMUM_CHARACTERS', { count: 3000 }))
      .optional(),
    files: z
      .object({
        docLink: z.string(),
        loadProgress: z.number().max(100),
        abort: z.function(),
        id: z.string(),
        doc: z
          .custom<File>()
          .or(
            z.object({
              size: z.number(),
              type: z.string(),
              name: z.string(),
            }),
          )
          .refine(
            ({ type }) => ACCEPTED_FILE_MIME_TYPES.includes(type),
            getTranslation('FILE_FORMAT_NOT_SUPPORTED'),
          )
          .refine(({ size }) => size <= MAX_FILE_SIZE, fileSchemaMessage),
      })
      .array()

      .optional(),
  })
  .optional();

export const formSchema = z.object({
  stepOne: formSchemaStepOne,
  stepTwo: fromSchemaStepTwo,
  stepThree: formSchemaStepThree,
  stepFour: fromSchemaStepFour,
});

export type FormSchema = z.infer<typeof formSchema>;
